const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const nextTask = () => new Promise((resolve) => setTimeout(resolve, 0))

export function getMotionStabilizerSettings() {
  return {
    enabled: Boolean(document.getElementById("motionStabilizerToggle")?.checked),
    strength: clamp(Number(document.getElementById("stabilizerStrengthSlider")?.value) || 0, 0, 400) / 100,
    smoothingRadius: clamp(Math.round(Number(document.getElementById("stabilizerSmoothingSlider")?.value) || 24), 1, 120),
    maxCorrection: clamp(Number(document.getElementById("stabilizerMaxCorrectionSlider")?.value) || 25, 1, 50) / 100,
    maxRotation: clamp(Number(document.getElementById("stabilizerMaxRotationSlider")?.value) || 15, 0, 30),
  }
}

function analysisDimensions(width, height) {
  const scale = Math.min(1, 224 / Math.max(1, width), 144 / Math.max(1, height))
  return {
    width: Math.max(64, Math.round(width * scale)),
    height: Math.max(48, Math.round(height * scale)),
  }
}

function featureFrame(image, width, height) {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true })
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = "medium"
  context.drawImage(image, 0, 0, width, height)
  const rgba = context.getImageData(0, 0, width, height).data
  const luma = new Uint8Array(width * height)
  for (let source = 0, target = 0; source < rgba.length; source += 4, target += 1) {
    luma[target] = (rgba[source] * 77 + rgba[source + 1] * 150 + rgba[source + 2] * 29) >> 8
  }
  const features = new Uint8Array(width * height)
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x
      const horizontal = Math.abs(luma[index + 1] - luma[index - 1])
      const vertical = Math.abs(luma[index + width] - luma[index - width])
      const diagonalA = Math.abs(luma[index + width + 1] - luma[index - width - 1])
      const diagonalB = Math.abs(luma[index + width - 1] - luma[index - width + 1])
      features[index] = clamp(horizontal + vertical + (diagonalA + diagonalB) / 3, 0, 255)
    }
  }
  return features
}

function transformScore(previous, current, width, height, dx, dy, angleDegrees, step, margin) {
  const angle = angleDegrees * Math.PI / 180
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  const centerX = (width - 1) / 2
  const centerY = (height - 1) / 2
  let score = 0
  let weight = 0

  for (let y = margin; y < height - margin; y += step) {
    for (let x = margin; x < width - margin; x += step) {
      const relativeX = x - centerX
      const relativeY = y - centerY
      const transformedX = Math.round(centerX + relativeX * cosine - relativeY * sine + dx)
      const transformedY = Math.round(centerY + relativeX * sine + relativeY * cosine + dy)
      if (transformedX < 1 || transformedX >= width - 1 || transformedY < 1 || transformedY >= height - 1) continue
      const sourceValue = previous[y * width + x]
      const targetValue = current[transformedY * width + transformedX]
      const importance = 16 + Math.max(sourceValue, targetValue)
      score += Math.abs(sourceValue - targetValue) * importance
      weight += importance
    }
  }
  return weight ? score / weight : Infinity
}

function coarseAngles(maxRotation) {
  const search = Math.min(12, Math.max(3, maxRotation))
  return [-search, -search / 2, -search / 4, 0, search / 4, search / 2, search]
}

export function estimateTransform(previous, current, width, height, maxShift, maxRotation = 15) {
  const margin = 4
  let best = { x: 0, y: 0, angle: 0, score: Infinity }
  const coarseStep = maxShift >= 24 ? 4 : maxShift >= 10 ? 3 : maxShift >= 5 ? 2 : 1
  const scoreStep = maxShift >= 20 ? 6 : 4

  for (const angle of coarseAngles(maxRotation)) {
    for (let dy = -maxShift; dy <= maxShift; dy += coarseStep) {
      for (let dx = -maxShift; dx <= maxShift; dx += coarseStep) {
        const rawScore = transformScore(previous, current, width, height, dx, dy, angle, scoreStep, margin)
        const score = rawScore + Math.abs(angle) * 1.25
        if (score < best.score) best = { x: dx, y: dy, angle, score }
      }
    }
  }

  const coarse = best
  const translationRadius = Math.max(2, coarseStep)
  const angleRadius = Math.min(2, Math.max(0.75, maxRotation / 12))
  const angleStep = maxRotation > 20 ? 0.5 : 0.25
  for (let angle = coarse.angle - angleRadius; angle <= coarse.angle + angleRadius + 1e-6; angle += angleStep) {
    if (Math.abs(angle) > maxRotation) continue
    for (let dy = coarse.y - translationRadius; dy <= coarse.y + translationRadius; dy += 1) {
      if (dy < -maxShift || dy > maxShift) continue
      for (let dx = coarse.x - translationRadius; dx <= coarse.x + translationRadius; dx += 1) {
        if (dx < -maxShift || dx > maxShift) continue
        const rawScore = transformScore(previous, current, width, height, dx, dy, angle, 3, margin)
        const score = rawScore + Math.abs(angle) * 1.25
        if (score < best.score) best = { x: dx, y: dy, angle, score }
      }
    }
  }
  return best
}

function smoothPath(path, radius) {
  return path.map((_, index) => {
    let totalWeight = 0
    let x = 0
    let y = 0
    let angle = 0
    const start = Math.max(0, index - radius)
    const end = Math.min(path.length - 1, index + radius)
    for (let sample = start; sample <= end; sample += 1) {
      const distance = Math.abs(sample - index)
      const weight = (radius + 1 - distance) ** 2
      totalWeight += weight
      x += path[sample].x * weight
      y += path[sample].y * weight
      angle += path[sample].angle * weight
    }
    return { x: x / totalWeight, y: y / totalWeight, angle: angle / totalWeight }
  })
}

async function detachCanvas(canvas) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(canvas)
    } catch {
      // Canvas fallback below.
    }
  }
  const copy = document.createElement("canvas")
  copy.width = canvas.width
  copy.height = canvas.height
  copy.getContext("2d", { alpha: false }).drawImage(canvas, 0, 0)
  return copy
}

function reportProgress(done, total, stage) {
  dispatchEvent(new CustomEvent("converter-stabilizer-progress", {
    detail: { done, total, stage },
  }))
}

export async function stabilizeImageMap(images, orderedIndices, dimensions, options = {}) {
  const settings = getMotionStabilizerSettings()
  const indices = orderedIndices.filter((index) => images.has(index))
  if (!settings.enabled || settings.strength <= 0 || indices.length < 3) return images

  const analysis = analysisDimensions(dimensions.width, dimensions.height)
  const maxShift = clamp(
    Math.round(Math.min(analysis.width, analysis.height) * settings.maxCorrection),
    3,
    32,
  )
  const frames = []
  reportProgress(0, indices.length * 2, "Analyzing strong translation and rotation")

  for (let index = 0; index < indices.length; index += 1) {
    if (options.isCancelled?.()) throw new DOMException("Operation cancelled.", "AbortError")
    frames.push(featureFrame(images.get(indices[index]), analysis.width, analysis.height))
    reportProgress(index + 1, indices.length * 2, "Analyzing strong translation and rotation")
    if (index % 3 === 2) await nextTask()
  }

  const path = [{ x: 0, y: 0, angle: 0 }]
  for (let index = 1; index < frames.length; index += 1) {
    if (options.isCancelled?.()) throw new DOMException("Operation cancelled.", "AbortError")
    const motion = estimateTransform(
      frames[index - 1],
      frames[index],
      analysis.width,
      analysis.height,
      maxShift,
      settings.maxRotation,
    )
    path.push({
      x: path[index - 1].x + motion.x,
      y: path[index - 1].y + motion.y,
      angle: path[index - 1].angle + motion.angle,
    })
    if (index % 2 === 0) await nextTask()
  }

  const radius = Math.min(settings.smoothingRadius, Math.max(1, indices.length - 1))
  const smoothed = smoothPath(path, radius)
  const scaleX = dimensions.width / analysis.width
  const scaleY = dimensions.height / analysis.height
  const maxX = dimensions.width * settings.maxCorrection
  const maxY = dimensions.height * settings.maxCorrection
  const corrections = path.map((point, index) => ({
    x: clamp((smoothed[index].x - point.x) * scaleX * settings.strength, -maxX, maxX),
    y: clamp((smoothed[index].y - point.y) * scaleY * settings.strength, -maxY, maxY),
    angle: clamp(
      (smoothed[index].angle - point.angle) * settings.strength,
      -settings.maxRotation,
      settings.maxRotation,
    ),
  }))
  const largestX = Math.max(...corrections.map((value) => Math.abs(value.x)), 0)
  const largestY = Math.max(...corrections.map((value) => Math.abs(value.y)), 0)
  const largestAngle = Math.max(...corrections.map((value) => Math.abs(value.angle)), 0)
  const translationZoom = Math.max(
    largestX * 2 / Math.max(1, dimensions.width),
    largestY * 2 / Math.max(1, dimensions.height),
  )
  const rotationZoom = Math.tan(Math.min(45, largestAngle) * Math.PI / 180) * 1.45
  const zoom = Math.min(3.5, 1.025 + translationZoom + rotationZoom)

  reportProgress(indices.length, indices.length * 2, "Rendering strongly stabilized frames")
  for (let index = 0; index < indices.length; index += 1) {
    if (options.isCancelled?.()) throw new DOMException("Operation cancelled.", "AbortError")
    const sourceIndex = indices[index]
    const source = images.get(sourceIndex)
    const canvas = document.createElement("canvas")
    canvas.width = dimensions.width
    canvas.height = dimensions.height
    const context = canvas.getContext("2d", { alpha: false })
    context.fillStyle = "#000"
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = "high"
    const correction = corrections[index]
    context.save()
    context.translate(dimensions.width / 2 + correction.x, dimensions.height / 2 + correction.y)
    context.rotate(correction.angle * Math.PI / 180)
    context.scale(zoom, zoom)
    context.drawImage(source, -dimensions.width / 2, -dimensions.height / 2, dimensions.width, dimensions.height)
    context.restore()
    const stabilized = await detachCanvas(canvas)
    source?.close?.()
    images.set(sourceIndex, stabilized)
    reportProgress(indices.length + index + 1, indices.length * 2, "Rendering strongly stabilized frames")
    if (index % 2 === 1) await nextTask()
  }

  dispatchEvent(new CustomEvent("converter-stabilizer-complete", {
    detail: {
      frames: indices.length,
      zoom,
      maximumCorrectionX: largestX,
      maximumCorrectionY: largestY,
      maximumRotation: largestAngle,
    },
  }))
  return images
}
